import type { AppConfig } from "../../types/appConfig";
import type {
  Training,
  TrainingsPaginationRequest,
  TrainingSearchRequest,
  TrainingAlertGroup,
  TrainingTemplate,
  TrainingTemplatesPaginationRequest,
  CreateTrainingTemplatePayload,
  TrainingTemplateExternalOptionsResponse,
  ExternalProviderType,
  TrainingCourse,
  Session,
} from "./Training.typed";
import type { CourseTemplate } from "../followup/Followup.typed";
import type { TheoryTestTemplate } from "../theoryTests/TheoryTest.typed";
import type { TrainingMailEvent } from "../mailTemplates/MailTemplate.typed";
import type { PaginatedResponse } from "../zekay/pagination";
import {
  HttpError,
  expectStatuses,
  fetchZekay,
  fetchZekayJson,
  parseErrorFromResponse,
} from "../zekay/httpClient";

async function parseListOrPaginated<T>(
  response: Response,
  paginationParams: { page?: number; limit?: number } | undefined,
  emptyLimit: number,
): Promise<PaginatedResponse<T> | T[]> {
  await expectStatuses(response, 200, 204);
  if (response.status === 204) {
    return paginationParams &&
      (paginationParams.page !== undefined || paginationParams.limit !== undefined)
      ? { data: [], total: 0, page: 1, limit: emptyLimit, total_pages: 0 }
      : [];
  }
  const result = (await response.json()) as unknown;
  if (
    paginationParams &&
    (paginationParams.page !== undefined || paginationParams.limit !== undefined)
  ) {
    return result as PaginatedResponse<T>;
  }
  return Array.isArray(result) ? result : (result as { data?: T[] }).data || [];
}

export class TrainingModel {
  zekayAPI: string;

  constructor(config: AppConfig) {
    this.zekayAPI = config.zekayAPI;
  }

  async getDoneTrainings(
    paginationParams?: TrainingsPaginationRequest,
  ): Promise<PaginatedResponse<Training> | Training[]> {
    const params = new URLSearchParams();
    if (paginationParams?.page !== undefined)
      params.append("page", paginationParams.page.toString());
    if (paginationParams?.limit !== undefined)
      params.append("limit", paginationParams.limit.toString());
    if (paginationParams?.template_id != null)
      params.append("template_id", String(paginationParams.template_id));
    if (paginationParams?.alert) params.append("alert", paginationParams.alert);

    const url = `${this.zekayAPI}/trainings/done${params.toString() ? "?" + params.toString() : ""}`;
    const response = await fetchZekay(url);
    return parseListOrPaginated<Training>(response, paginationParams, 10);
  }

  async getTrainingAlerts(): Promise<TrainingAlertGroup[]> {
    const data = await fetchZekayJson<TrainingAlertGroup[]>(`${this.zekayAPI}/trainings/alerts`);

    return Array.isArray(data) ? data : [];
  }

  async createTraining(training: Training): Promise<Training> {
    const response = await fetchZekay(this.zekayAPI + "/trainings", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(training),
    });
    await expectStatuses(response, 201);
    return (await response.json()) as Training;
  }

  async deleteTraining(training: Training): Promise<void> {
    const id = training.ID;
    if (id == null) throw new Error("Training ID requis pour la suppression");
    const response = await fetchZekay(`${this.zekayAPI}/trainings/${id}`, { method: "DELETE" });
    await expectStatuses(response, 200, 204);
  }

  async updateTraining(t: Training): Promise<void> {
    const id = t.ID;
    if (id == null) throw new Error("Training ID requis pour la mise à jour");
    const response = await fetchZekay(`${this.zekayAPI}/trainings/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(t),
    });
    await expectStatuses(response, 200, 204);
  }

  async sendTrainingMail(trainingId: number, event: TrainingMailEvent): Promise<void> {
    const response = await fetchZekay(`${this.zekayAPI}/trainings/${trainingId}/mails`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event }),
    });
    await expectStatuses(response, 204);
  }

  async batchIncrementTrainingSessions(trainingIds: number[]): Promise<number> {
    if (trainingIds.length === 0) return 0;
    const response = await fetchZekay(`${this.zekayAPI}/trainings/sessions/batch-increment`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ training_ids: trainingIds }),
    });
    await expectStatuses(response, 200);
    const body = (await response.json()) as { updated?: number };
    return body.updated ?? 0;
  }

  async validateUserQualification(t: Training): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + "/qualification", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(t),
    });
    await expectStatuses(response, 204);
  }

  async updateTrainingFile(trainingId: number, formData: FormData): Promise<void> {
    const response = await fetchZekay(`${this.zekayAPI}/trainings/${trainingId}/file`, {
      method: "POST",
      body: formData,
    });
    await expectStatuses(response, 204);
  }

  async deleteTrainingFile(trainingId: number, type: string): Promise<void> {
    const response = await fetchZekay(
      `${this.zekayAPI}/trainings/${trainingId}/file?type=${encodeURIComponent(type)}`,
      { method: "DELETE" },
    );
    await expectStatuses(response, 204);
  }

  async getTrainingTemplates(
    paginationParams?: TrainingTemplatesPaginationRequest,
  ): Promise<PaginatedResponse<TrainingTemplate> | TrainingTemplate[]> {
    const params = new URLSearchParams();
    if (paginationParams) {
      if (paginationParams.page !== undefined)
        params.append("page", paginationParams.page.toString());
      if (paginationParams.limit !== undefined)
        params.append("limit", paginationParams.limit.toString());
    }

    const url = `${this.zekayAPI}/trainings/templates${params.toString() ? "?" + params.toString() : ""}`;
    const response = await fetchZekay(url);
    return parseListOrPaginated<TrainingTemplate>(response, paginationParams, 50);
  }

  async updateTrainingTemplate(tt: TrainingTemplate): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + "/trainings/templates", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tt),
    });
    await expectStatuses(response, 204);
  }

  async deleteTrainingTemplate(id: number): Promise<void> {
    const response = await fetchZekay(`${this.zekayAPI}/trainings/templates/${id}`, {
      method: "DELETE",
    });
    if (response.status === 204) return;
    if (response.status === 404)
      throw new HttpError({ status: 404, message: "Template introuvable" });
    throw new HttpError(await parseErrorFromResponse(response));
  }

  async createTrainingTemplate(
    payload: CreateTrainingTemplatePayload,
  ): Promise<TrainingTemplate | void> {
    const response = await fetchZekay(this.zekayAPI + "/trainings/templates", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(response, 201, 204);
    if (response.status === 204) return;
    return (await response.json()) as TrainingTemplate;
  }

  async getTrainingTemplatesExternalOptions(
    type: ExternalProviderType,
  ): Promise<TrainingTemplateExternalOptionsResponse> {
    const response = await fetchZekay(
      `${this.zekayAPI}/trainings/templates/external/options/${encodeURIComponent(type)}`,
    );
    await expectStatuses(response, 200);
    return (await response.json()) as TrainingTemplateExternalOptionsResponse;
  }

  async getTrainingsByTemplateId(templateId: string): Promise<Training[]> {
    const response = await fetchZekay(
      this.zekayAPI + "/trainings/templates/" + templateId + "/trainings",
    );
    await expectStatuses(response, 200, 204);
    if (response.status === 204) return [];
    return (await response.json()) as Training[];
  }

  async getTrainingsByTemplateIdAndStatus(
    templateId: string,
    status: number,
    paginationParams?: TrainingsPaginationRequest,
  ): Promise<PaginatedResponse<Training> | Training[]> {
    const params = new URLSearchParams();
    if (paginationParams?.page !== undefined)
      params.append("page", paginationParams.page.toString());
    if (paginationParams?.limit !== undefined)
      params.append("limit", paginationParams.limit.toString());
    if (paginationParams?.alert) params.append("alert", paginationParams.alert);

    const url = `${this.zekayAPI}/trainings/templates/${templateId}/trainings/status/${String(status)}${params.toString() ? "?" + params.toString() : ""}`;
    const response = await fetchZekay(url);
    return parseListOrPaginated<Training>(response, paginationParams, 10);
  }

  async searchTrainings(
    searchRequest: TrainingSearchRequest,
  ): Promise<PaginatedResponse<Training> | Training[]> {
    const response = await fetchZekay(`${this.zekayAPI}/trainings/search`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(searchRequest),
    });
    await expectStatuses(response, 200, 204);
    if (response.status === 204) {
      return { data: [], total: 0, page: 1, limit: 10, total_pages: 0 };
    }
    return (await response.json()) as PaginatedResponse<Training>;
  }

  async getTrainingByUserTemplateId(
    templateId: number,
    userId: number,
  ): Promise<Training | undefined> {
    const response = await fetchZekay(
      this.zekayAPI + "/trainings/" + String(templateId) + "/" + String(userId),
    );
    await expectStatuses(response, 200, 204);
    if (response.status === 204) return undefined;
    return (await response.json()) as Training;
  }

  async getCourseTemplate(id: number): Promise<CourseTemplate | undefined> {
    const response = await fetchZekay(this.zekayAPI + "/courses/templates/" + id);
    await expectStatuses(response, 200, 204);
    if (response.status === 204) return undefined;
    return (await response.json()) as CourseTemplate;
  }

  async getTheoryTestTemplate(templateId: number): Promise<TheoryTestTemplate | undefined> {
    const response = await fetchZekay(this.zekayAPI + "/theorytests/templates/" + templateId);
    await expectStatuses(response, 200, 204);
    if (response.status === 204) return undefined;
    return (await response.json()) as TheoryTestTemplate;
  }

  async listOnlineTheoryTestTemplateIds(): Promise<number[]> {
    const response = await fetchZekay(this.zekayAPI + "/theorytests/templates/online");
    await expectStatuses(response, 200, 204);
    if (response.status === 204) return [];
    return (await response.json()) as number[];
  }

  async createCourseTemplate(ct: CourseTemplate): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + "/courses/templates/create", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(ct),
    });
    await expectStatuses(response, 204);
  }

  async updateCourseTemplate(ct: CourseTemplate): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + "/courses/templates/update", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(ct),
    });
    await expectStatuses(response, 204);
  }

  async createSession(session: Session): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + "/courses/session/create", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(session),
    });
    await expectStatuses(response, 204);
  }

  async updateSession(s: Session): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + "/courses/session/update", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(s),
    });
    await expectStatuses(response, 204);
  }

  async generateSessionPDF(templateId: number, customerId: number): Promise<void> {
    const response = await fetchZekay(
      this.zekayAPI + "/courses/session/generate/" + templateId + "/" + customerId,
    );
    await expectStatuses(response, 200, 204);
  }

  async deleteSession(templateId: number, customerId: number, number: number): Promise<void> {
    const response = await fetchZekay(
      `${this.zekayAPI}/courses/session/${templateId}/${customerId}/${number}`,
      { method: "DELETE" },
    );
    await expectStatuses(response, 204);
  }

  async updateTheoryTestTemplate(template: TheoryTestTemplate): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + "/theorytests/templates/update", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(template),
    });
    await expectStatuses(response, 204);
  }

  async createTheoryTestTemplate(template: TheoryTestTemplate): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + "/theorytests/templates/create", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(template),
    });
    await expectStatuses(response, 204);
  }

  async uploadTrainingTemplateResource(
    templateId: number,
    field: "course1" | "course2" | "theory" | "agreement" | "disclaimer",
    file: Blob,
  ): Promise<TrainingTemplate> {
    const form = new FormData();
    try {
      const name = typeof File !== "undefined" && file instanceof File ? file.name : undefined;
      const mime = file.type;
      const isPdf =
        mime === "application/pdf" ||
        (typeof name === "string" && name.toLowerCase().endsWith(".pdf"));
      if (!isPdf) {
        throw new Error("Seuls les fichiers PDF sont acceptés.");
      }
    } catch {
      throw new Error("Seuls les fichiers PDF sont acceptés.");
    }
    const filename = typeof File !== "undefined" && file instanceof File ? file.name : undefined;
    if (filename) form.append("File", file, filename);
    else form.append("File", file);
    const response = await fetchZekay(
      `${this.zekayAPI}/trainings/templates/${templateId}/upload/${field}`,
      {
        method: "POST",
        body: form,
      },
    );
    await expectStatuses(response, 200);
    return (await response.json()) as TrainingTemplate;
  }

  async listTrainingCourses(templateId: number): Promise<TrainingCourse[]> {
    const response = await fetchZekay(`${this.zekayAPI}/trainings/templates/${templateId}/courses`);
    await expectStatuses(response, 200, 204, 404);
    if (response.status === 200) return (await response.json()) as TrainingCourse[];
    return [];
  }

  async addTrainingCourse(templateId: number, file: Blob, name?: string): Promise<TrainingCourse> {
    const form = new FormData();
    const filename = typeof File !== "undefined" && file instanceof File ? file.name : undefined;
    if (filename) form.append("File", file, filename);
    else form.append("File", file);
    if (name) form.append("Name", name);
    const response = await fetchZekay(
      `${this.zekayAPI}/trainings/templates/${templateId}/courses`,
      {
        method: "POST",
        body: form,
      },
    );
    await expectStatuses(response, 201);
    return (await response.json()) as TrainingCourse;
  }

  async deleteTrainingCourse(templateId: number, courseId: string): Promise<void> {
    const response = await fetchZekay(
      `${this.zekayAPI}/trainings/templates/${templateId}/courses/${encodeURIComponent(String(courseId))}`,
      { method: "DELETE" },
    );
    await expectStatuses(response, 204);
  }

  async replaceTrainingCourses(templateId: number, courses: TrainingCourse[]): Promise<void> {
    const response = await fetchZekay(
      `${this.zekayAPI}/trainings/templates/${templateId}/courses`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(courses),
      },
    );
    await expectStatuses(response, 204);
  }
}
